import * as Yup from 'yup';

import { VOUCHER_DISCOUNT_TYPES, VOUCHER_MAXIMUM_DISCOUNT_TYPE, VOUCHER_STATUSES } from 'constants/index';
import {
  ACCENTED_REGEX,
  IS_SPECIAL_ACCEPT,
  LETTER_WITH_SPACE_REGEX,
  NUMBER_WITH_PERIOD,
  PHONE_REGEX,
  WORDS_AND_NUMBERS_REGEX,
  EMAIL_REGEX,
  NUMBER_ONLY_REGEX,
  PASSWORD_IS_HAVE_SPECIAL,
  PASSWORD_REGEX,

  textLengthLimit,
  NUMBER_ONLY,
  CHECK_MORE_THAN_2_WORDS,
  UPPERCASE_WITH_SPACE_AND_NO_NUMBER_REGEX,
} from 'constants/validationHelper';
import { areDatesConsecutive, invalidNumberArea, parseCurrency } from 'utils';

export const getRequiredText = (field) => `${field} không được để trống`;
export const getMinText = (field, min) => `${field} không được ít hơn ${min} ký tự`;
export const getMaxText = (field, max) => `${field} không được vượt quá ${max} ký tự`;
export const getTimeLaterThanNowText = (field) => `Vui lòng chọn thời gian ${field} muộn hơn thời gian hiện tại`;
export const getInvalidText = (field) => `${field} không hợp lệ`;
export const getInvalidLengthPassword = (minLength, maxLength) => `Mật khẩu phải dài từ ${minLength} đến ${maxLength} ký tự, bao gồm chữ và số`;

const LOGIN_PASSWORD_MESSAGE = 'Mật khẩu phải dài từ 8 đến 32 ký tự, bao gồm chữ, số và ký tự đặc biệt';

export const userValidation = {
  firstName: Yup.string().required(({
    type: 'is_required',
    name: 'profile.first_name',
  }))
    .trim()
    .matches(LETTER_WITH_SPACE_REGEX, ({
      message: {
        type: 'letter_only',
        name: 'profile.first_name',
      },
      excludeEmptyString: true,
    }))
    .max(textLengthLimit.VERY_SHORT, ({
      type: 'max_length',
      name: 'profile.first_name',
      number: textLengthLimit.VERY_SHORT,
    })),

  lastName: Yup.string()
    .trim()
    .matches(LETTER_WITH_SPACE_REGEX, ({
      message: {
        type: 'letter_only',
        name: 'profile.last_name',
      },
      excludeEmptyString: true,
    }))
    .max(textLengthLimit.VERY_SHORT, ({
      type: 'max_length',
      name: 'profile.last_name',
      number: textLengthLimit.VERY_SHORT,
    })),

  middleName: Yup.string()
    .trim()
    .matches(LETTER_WITH_SPACE_REGEX, ({
      message: {
        type: 'letter_only',
        name: 'auth.middle_name',
      },
      excludeEmptyString: true,
    }))
    .max(textLengthLimit.VERY_SHORT, ({
      type: 'max_length',
      name: 'auth.middle_name',
      number: textLengthLimit.VERY_SHORT,
    })),

  cardId: Yup.string()
    .max(textLengthLimit.VERY_SHORT, ({
      type: 'max_length',
      name: 'profile.id_number_passport',
      number: textLengthLimit.VERY_SHORT,
    }))
    .matches(ACCENTED_REGEX, {
      message: {
        type: 'wrong_format',
        name: 'profile.id_number_passport',
      },
      excludeEmptyString: true,
    }),

  address: Yup.string()
    .trim()
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'profile.address',
      number: textLengthLimit.SHORT,
    })),

  phoneNumber: Yup.string()
    .trim()
    .matches(PHONE_REGEX, ({
      message: {
        type: 'special_characters',
        name: 'profile.phone_number',
        number: textLengthLimit.SHORT,
      },
      excludeEmptyString: true,
    }))
    .max(textLengthLimit.PHONE_NUMBER, ({
      type: 'max_length',
      name: 'profile.phone_number',
      number: textLengthLimit.PHONE_NUMBER,
    })),

  businessEmail: Yup.string()
    .trim()
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'profile.business_email',
      number: textLengthLimit.SHORT,
    }))
    .email(({
      type: 'wrong_format',
      name: 'profile.email',
    })),

  email: Yup.string()
    .trim()
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'profile.email',
      number: textLengthLimit.SHORT,
    }))
    .email(({
      type: 'wrong_format',
      name: 'profile.email',
    })),
};

export const accountValidation = {
  email: Yup.string()
    .required({
      type: 'is_required',
      name: 'Email',
    })
    .email({
      type: 'wrong_format',
      name: 'Email',
    })
    .max(textLengthLimit.SHORT, {
      type: 'max_length',
      name: 'Email',
      number: textLengthLimit.SHORT,
    }),

  phoneNumber: Yup.string()
    .required(({
      type: 'is_required',
      name: 'auth.username',
    }))
    .trim()
    .max(textLengthLimit.SHORT, {
      type: 'max_length',
      name: 'auth.username',
      number: textLengthLimit.SHORT,
    }),

  password: Yup.string()
    .required(({
      type: 'is_required',
      name: 'auth.password',
    }))
    .trim()
    .min(textLengthLimit.PASSWORD, {
      type: 'min_length',
      name: 'auth.password',
      number: textLengthLimit.PASSWORD,
    })
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'auth.password',
      number: textLengthLimit.SHORT,
    }))
    .matches(IS_SPECIAL_ACCEPT, {
      message: {
        type: 'no_space',
        name: 'auth.password',
      },
      excludeEmptyString: true,
    }),

  confirmPassword: Yup.string().required(({
    type: 'is_required',
    name: 'auth.password',
  }))
    .oneOf([Yup.ref('password'), null], ({
      type: 'confirm_password',
    }))
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'auth.password',
      number: textLengthLimit.SHORT,
    })),

  isPrivacyPolicy: Yup.bool().required(('register.msg_accept_terms'))
    .oneOf([true], ('register.msg_accept_terms')),
};

export const passwordValidation = {
  password: Yup.string().required(({
    type: 'is_required',
    name: 'auth.current_password',
  }))
    .trim()
    .min(textLengthLimit.PASSWORD, ({
      type: 'min_length',
      name: 'auth.current_password',
      number: textLengthLimit.PASSWORD,
    }))
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'current_password',
      number: textLengthLimit.SHORT,
    })),

  newPassword: Yup.string().required(({
    type: 'is_required',
    name: 'auth.new_password',
  }))
    .trim()
    .min(textLengthLimit.PASSWORD, ({
      type: 'min_length',
      name: 'auth.new_password',
      number: textLengthLimit.PASSWORD,
    }))
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'auth.new_password',
      number: textLengthLimit.SHORT,
    }))
    .matches(IS_SPECIAL_ACCEPT, {
      message: {
        type: 'no_space',
        name: 'validation.profile.new_password',
      },
      excludeEmptyString: true,
    }),

  confirmPassword: Yup.string().required(({
    type: 'is_required',
    name: 'auth.re_enter_new_password',
  }))
    .trim()
    .oneOf([Yup.ref('newPassword'), null], ({
      type: 'wrong_password',
      name: 'auth.re_enter_new_password',
    }))
    .min(textLengthLimit.PASSWORD, ({
      type: 'min_length',
      name: 'auth.re_enter_new_password',
      number: textLengthLimit.PASSWORD,
    }))
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'auth.re_enter_new_password',
      number: textLengthLimit.SHORT,
    }))
    .matches(IS_SPECIAL_ACCEPT, {
      message: {
        type: 'no_space',
        name: 'validation.profile.re_enter_new_password',
      },
      excludeEmptyString: true,
    }),
};

export const mediaValidation = {
  name: Yup.string()
    .trim()
    .max(textLengthLimit.SHORT, ({
      type: 'max_length',
      name: 'name',
      number: textLengthLimit.SHORT,
    })),
};

export const productEditValidation = {
  name: Yup.string()
    .required('Tên sản phẩm không được để trống')
    .min(10, 'Tên sản phẩm hợp lệ từ 10-120 ký tự')
    .max(120, 'Tên sản phẩm hợp lệ từ 10-120 ký tự')
    .trim(),

  price: Yup.string()
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000',
      excludeEmptyString: true,
    })
    .test({
      message: 'Giá không được để trống',
      test(value) {
        const { variants } = this.parent;
        if (!value && !variants?.length) return false;

        return true;
      },
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000',
      test(value) {
        const { variants } = this.parent;
        const number = parseCurrency(value);

        if (!variants?.length && invalidNumberArea(number, 1000, 120000000)) return false;
        return true;
      },
    }),

  quantity: Yup.string()
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 0 đến 10.000.000',
      excludeEmptyString: true,
    })
    .test({
      message: 'Kho hàng không được để trống',
      test(value) {
        const { variants } = this.parent;
        if (!value && !variants?.length) return false;

        return true;
      },
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 0 đến 10.000.000',
      test(value) {
        const number = parseCurrency(value);

        if (value && invalidNumberArea(number, 0, 10000000)) return false;
        return true;
      },
    }),

  isVariantImageErr: Yup.bool()
    .test({
      message: 'Bạn phải thêm tất cả các hình ảnh phân loại hàng hoặc để trống tất cả chúng',
      test() {
        const { variants } = this.from[0].value;
        const variantWithoutImage = variants[0]?.values.filter(
          (item) => item.variantName && !item.image,
        ).length;
        const validVariant = variants[0]?.values.filter((item) => item.variantName).length;

        if (variantWithoutImage === 0
          || variantWithoutImage === validVariant) return true;

        return false;
      },
    }),

  isShippingUnitErr: Yup.bool()
    .test({
      message: 'Sản phẩm không thể lưu vì chưa có kênh vận chuyển nào được bật',
      test() {
        const { logisticsChannels } = this.from[0].value;

        const selectedChannel = logisticsChannels.find((item) => !item.disabled);

        if (selectedChannel) return true;

        return false;
      },
    }),

  isDuplicateGroupVariantName: Yup.bool()
    .test({
      message: 'Tên phân loại hàng phải khác nhau',
      test() {
        const { variants } = this.from[0].value;

        if (variants?.length > 1
          && variants[1].name
          && (variants[0].name.toLowerCase() === variants[1].name.toLowerCase())) return false;

        return true;
      },
    }),

  variants: Yup.array()
    .of(Yup.object()
      .shape({
        name: Yup.string()
          .required('Tên nhóm phân loại không được để trống')
          .max(14, 'Tên nhóm phân loại không được vượt quá 14 ký tự')
          .trim(),
        values: Yup.array()
          .of(
            Yup.object().shape({
              variantName: Yup.string()
                .required('Phân loại hàng không được để trống')
                .max(20, 'Phân loại hàng không được quá 20 ký tự')
                .trim(),
            }),
          )
          .max(50, 'Phân loại hàng không được vượt quá 50'),
      }))
    .test({
      message: 'Số lượng nhóm phân loại không được vượt quá 2',
      test(value = []) {
        if (value.length > 2) return false;
        return true;
      },
    }),

  productVariants: Yup.array()
    .of(Yup.object().shape({
      sku: Yup.string()
        .max(100, 'SKU sản phẩm không vượt quá 100 ký tự')
        .trim(),

      name: Yup.string()
        .trim(),

      quantity: Yup.string()
        .matches(NUMBER_WITH_PERIOD, {
          message: 'Vui lòng nhập vào số nguyên từ 0 đến 10.000.000',
          excludeEmptyString: true,
        })
        .test({
          message: 'Kho hàng không được để trống',
          test(value) {
            if (!value) return false;

            return true;
          },
        })
        .test({
          message: 'Vui lòng nhập vào số nguyên từ 0 đến 10.000.000',
          test(value) {
            const number = parseCurrency(value);

            if (value && invalidNumberArea(number, 0, 10000000)) return false;
            return true;
          },
        }),

      price: Yup.string()
        .matches(NUMBER_WITH_PERIOD, {
          message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000',
          excludeEmptyString: true,
        })
        .test({
          message: 'Giá không được để trống',
          test(value) {
            if (!value) return false;

            return true;
          },
        })
        .test({
          message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000',
          test(value) {
            const number = parseCurrency(value);

            if (value && invalidNumberArea(number, 1000, 120000000)) return false;
            return true;
          },
        }),

      tier: Yup.array().of(Yup.string()),
    })),

  description: Yup.string()
    .required('Mô tả không được để trống')
    .min(100, 'Mô tả sản phẩm hợp lệ từ 100-3000 ký tự')
    .max(3000, 'Mô tả sản phẩm hợp lệ từ 100-3000 ký tự')
    .trim(),

  imagePreview: Yup.object()
    .required('Ảnh bìa không được để trống'),

  brand: Yup.string()
    .required('Thương hiệu không được để trống'),

  variantGroupName: Yup.string()
    .required('Tên nhóm phân loại không được để trống')
    .max(14, 'Tên nhóm phân loại không được vượt quá 14 ký tự'),

  variantName: Yup.string()
    .required('Phân loại hàng không được để trống')
    .max(20, 'Phân loại hàng không được vượt quá 20 ký tự'),

  sku: Yup.string()
    .max(100, 'SKU phân loại không vượt quá 100 ký tự'),

  weight: Yup.string()
    .required('Cân nặng không được để trống')
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      excludeEmptyString: true,
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      test(value) {
        const number = parseCurrency(value);

        if (value && invalidNumberArea(number, 1, 1000000)) return false;
        return true;
      },
    }),

  length: Yup.string()
    .required('Chiều dài không được để trống')
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      excludeEmptyString: true,
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      test(value) {
        const number = parseCurrency(value);

        if (value && invalidNumberArea(number, 1, 1000000)) return false;
        return true;
      },
    }),

  width: Yup.string()
    .required('Chiều rộng không được để trống')
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      excludeEmptyString: true,
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      test(value) {
        const number = parseCurrency(value);

        if (value && invalidNumberArea(number, 1, 1000000)) return false;
        return true;
      },
    }),

  height: Yup.string()
    .required('Chiều cao không được để trống')
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      excludeEmptyString: true,
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 1 đến 1.000.000 ',
      test(value) {
        const number = parseCurrency(value);

        if (value && invalidNumberArea(number, 1, 1000000)) return false;
        return true;
      },
    }),

  priceAll: Yup.string()
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000',
      excludeEmptyString: true,
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000',
      test(value) {
        const number = parseCurrency(value);

        if (value && invalidNumberArea(number, 1000, 120000000)) return false;
        return true;
      },
    }),

  quantityAll: Yup.string()
    .matches(NUMBER_WITH_PERIOD, {
      message: 'Vui lòng nhập vào số nguyên từ 0 đến 10.000.000',
      excludeEmptyString: true,
    })
    .test({
      message: 'Vui lòng nhập vào số nguyên từ 0 đến 10.000.000',
      test(value) {
        const number = parseCurrency(value);

        if (value && invalidNumberArea(number, 0, 10000000)) return false;
        return true;
      },
    }),
};

export const voucherCreateValidation = {
  name: Yup.string().trim()
    .required(getRequiredText('Tên'))
    .max(100, getMaxText('Tên', 100)),
  code: Yup.string().trim()
    .required(getRequiredText('Mã Voucher'))
    .min(5, 'Mã Voucher hợp lệ từ 5-9 ký tự, chỉ bao gồm chữ cái (A-Z), số (0-9)')
    .max(9, 'Mã Voucher hợp lệ từ 5-9 ký tự, chỉ bao gồm chữ cái (A-Z), số (0-9)')
    .matches(WORDS_AND_NUMBERS_REGEX, 'Mã Voucher hợp lệ từ 5-9 ký tự, chỉ bao gồm chữ cái (A-Z), số (0-9)'),
  startAt: Yup.date()
    .test(
      'startAt',
      getRequiredText('Thời gian bắt đầu'),
      (value, context) => {
        const { isEdit, voucherStatusCode } = context.parent;
        const date = new Date();

        if (!value?.getTime?.()) {
          return false;
        }

        if (date.getTime() > value.getTime()
          && (!isEdit || (isEdit && voucherStatusCode === VOUCHER_STATUSES.UPCOMING.code))) {
          return context.createError({ message: getTimeLaterThanNowText('bắt đầu') });
        }

        return value && value?.getTime?.();
      },
    ),
  expiredAt: Yup.date()
    .test(
      'end_after_start_1_hour',
      'Chương trình phải kéo dài ít nhất là 1h kể từ khi bắt đầu',
      (value, context) => {
        const { startAt, voucherStatusCode, isEdit } = context.parent;
        const date = new Date();

        if (!value || !value?.getTime?.()) {
          return context.createError({ message: getRequiredText('Thời gian kết thúc') });
        }

        if (!startAt?.getTime?.()) {
          return false;
        }

        if (date.getTime() > value.getTime()
          && (!isEdit || (isEdit && voucherStatusCode >= VOUCHER_STATUSES.UPCOMING.code))) {
          return context.createError({ message: getTimeLaterThanNowText('kết thúc') });
        }

        return value.getTime() - startAt.getTime() >= 3600000;
      },
    ),
  // discountType: Yup.string().required(getRequiredText('Mức giảm')),
  discount: Yup.string()
    .required(getRequiredText('Mức giảm'))
    .test(
      'reduction_number',
      'Vui lòng nhập vào giá trị giữa 1 và 99 ',
      (value, context) => {
        const { discountType } = context.parent;
        const discount = parseCurrency(value);

        if (discountType === VOUCHER_DISCOUNT_TYPES.PERCENT) {
          return (discount >= 1 && discount <= 99);
        }

        if (discount < 1000 || discount > 120000000) {
          return context.createError({ message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000' });
        }

        return true;
      },
    ),
  maximumDiscount: Yup.string()
    .test(
      'maximum_reduction_number',
      getRequiredText('Mức giảm tối đa'),
      (value, context) => {
        const { discountType, maximumDiscountType } = context.parent;
        const maximumDiscount = parseCurrency(value);

        if (discountType === VOUCHER_DISCOUNT_TYPES.PERCENT
          && maximumDiscountType === VOUCHER_MAXIMUM_DISCOUNT_TYPE.LIMITED) {
          if (!maximumDiscount) {
            return false;
          }

          if (maximumDiscount < 1000 || maximumDiscount > 120000000) {
            return context.createError({ message: 'Vui lòng nhập vào số nguyên từ 1.000 đến 120.000.000' });
          }

          return !!maximumDiscount;
        }

        return true;
      },
    ),
  orderMinimalAmount: Yup.string()
    .required(getRequiredText('Giá trị đơn hàng tối thiểu'))
    .test(
      'minimum_order_value',
      'Giá trị voucher không thể vượt quá giá trị tối thiểu của đơn hàng',
      (value, context) => {
        const { discountType } = context.parent;
        const discount = parseCurrency(context.parent.discount);
        const orderMinimalAmount = parseCurrency(value);

        if (orderMinimalAmount < 0 || orderMinimalAmount > 120000000) {
          return context.createError({ message: 'Vui lòng nhập vào số nguyên từ 0 đến 120.000.000' });
        }

        if (discountType === VOUCHER_DISCOUNT_TYPES.PRICE) {
          return orderMinimalAmount >= discount;
        }

        return true;
      },
    ),
  quantity: Yup.string()
    .required(getRequiredText('Tổng số lượng mã giảm giá'))
    .test(
      'number_of_vouchers',
      'Vui lòng nhập vào số nguyên từ 1 đến 200.000',
      (value) => {
        const quantity = parseCurrency(value);

        return quantity >= 1 && quantity <= 200000;
      },
    ),
  cost: Yup.string()
    .required(getRequiredText('Giá mua (V-Token)'))
    .test(
      'cost_format',
      'Ký tự đầu và ký tự cuối phải là ký tự số',
      (value) => (value?.[value?.length - 1] !== ',' && value?.[0] !== ','),
    ),
  condition: Yup.string().required(getRequiredText('Điều kiện sử dụng')),
};

export const widthDrawValidation = {
  price: Yup.string().trim()
    .required(getRequiredText('Giá trị yêu cầu thanh toán'))
    .test({
      message: 'Vui lòng nhập vào số nguyên lớn hơn 0',
      test(value) {
        const number = parseCurrency(value);

        return number > 0;
      },
    })
    .test({
      message: 'Giá trị giao dịch tối thiểu là 100.000 đ',
      test(value) {
        const number = parseCurrency(value);

        return number >= 100000;
      },
    })
    .test({
      message: 'Giá trị giao dịch tối đa là 300.000.000 đ',
      test(value) {
        const number = parseCurrency(value);

        return number <= 300000000;
      },
    }),
};

export const loginAdminValidation = {
  email: Yup.string()
    .required(getRequiredText('Email'))
    .max(textLengthLimit.SHORT, getMaxText('Email', textLengthLimit.SHORT))
    .matches(EMAIL_REGEX, getInvalidText('Email')),

  usernameOrEmail: Yup.string()
    .required(getRequiredText('Tên đăng nhập / Email'))
    .max(textLengthLimit.SHORT, getMaxText('Tên đăng nhập / Email', textLengthLimit.SHORT)),

  password: Yup.string()
    .trim()
    .required(getRequiredText('Mật khẩu'))
    .min(textLengthLimit.PASSWORD, LOGIN_PASSWORD_MESSAGE)
    .max(textLengthLimit.PASSWORD_MAX, LOGIN_PASSWORD_MESSAGE)
    .matches(PASSWORD_REGEX, LOGIN_PASSWORD_MESSAGE),
};

export const loginAdminStep2Validation = {
  code: Yup.string()
    .trim()
    .required(getRequiredText('Mã OTP'))
    .min(textLengthLimit.CODE, getMinText('Mã OTP', textLengthLimit.CODE))
    .max(textLengthLimit.CODE, getMaxText('Mã OTP', textLengthLimit.CODE))
    .matches(NUMBER_ONLY_REGEX, getInvalidText('Mã OTP')),
};

export const editLocationFormSteps = {
  stepBasicValidation: {
    placeType: Yup.string().trim().required('Loại hình không được để trống'),
    name: Yup.string().trim().required('Tên chỗ nghỉ không được để trống').max(100, 'Tên chỗ nghỉ không quá 100 ký tự'),
    starRating: Yup.string().trim().required('Hạng sao là số nguyên trong khoảng 0 đến 5'),
    placeContactName: Yup.string().trim()
      .test(
        'name_format',
        'Tên người liên hệ không được để trống',
        (value, context) => {
          if (!value || !value.length) return false;

          if (value.split(' ').length < 2) {
            return context.createError({ message: 'Tên người liên hệ gồm 2 từ trở lên' });
          }
          // regex only characters
          if (!value.match(LETTER_WITH_SPACE_REGEX)) {
            return context.createError({ message: 'Tên người liên hệ không chứa ký tự đặc biệt và số' });
          }

          return value.length < 101;
        },
      ),
    placeContactMainLanguage: Yup.string().trim().required('Ngôn ngữ ưu tiên không được để trống'),
    placeContactRole: Yup.string().trim().required('Chức vụ không được để trống'),
    placeContactEmail: Yup.string().trim().required('Email không được để trống').email('Email không hợp lệ'),
    placeContactPhone: Yup.string().trim().required('Số điện thoại không được để trống').matches(
      PHONE_REGEX,
      'Số điện thoại không hợp lệ',
    ),
    placeContactAnotherPhone: Yup.string().trim().matches(
      PHONE_REGEX,
      'Số điện thoại không hợp lệ',
    ),
    market: Yup.string().trim().required('Thị trường không được để trống'),
    province: Yup.string().trim().required('Tỉnh/ Thành phố không được để trống'),
    // district: Yup.string().trim().required('Quận/ Huyện không được để trống'),
    ward: Yup.string().trim(),
    address: Yup.string().trim().required('Địa chỉ không được để trống').test(
      'address_format',
      'Địa chỉ không quá 200 ký tự',
      (value, ctx) => {
        if (!value || !value.length) return false;

        if (value.split(' ').length < 2) {
          return ctx.createError({ message: 'Địa chỉ gồm 2 từ trở lên' });
        }

        return value.length < 201;
      },
    ),
    landmarks: Yup.array(),
    coordinates: Yup.string()
      .trim()
      .required('Định vị không được để trống')
      .test(
        'coordinates_format',
        'Nhập lat trước long sau',
        (value, ctx) => {
          const coordinates = value?.split(',').map((coord) => coord.trim());

          if (coordinates?.length !== 2 || !coordinates[0] || !coordinates[1]) {
            return ctx.createError({ message: 'Định dạng gồm lat, long' });
          }

          const isLatitudeValid = /^[-+]?((1[0-7]|[1-9])?\d(\.\d+)?|180(\.0+)?)$/.test(coordinates[1]);
          const isLongitudeValid = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/.test(coordinates[0]);

          if (!isLatitudeValid || !isLongitudeValid
          ) {
            return ctx.createError({ message: 'Vui lòng nhập định dạng lat, long' });
          }

          return true;
        },
      ),
    description: Yup.string().trim().required('Giới thiệu chổ nghĩ không được để trống').max(2000, 'Giới thiệu chỗ nghỉ không quá 2000 ký tự'),
  },
  stepRoomInformation: {
    roomName: Yup.string().trim(),
    name: Yup.string()
      .trim()
      .required('Tên phòng không được để trống')
      .max(100, 'Tên hiển thị không quá 100 ký tự')
      .test(
        'name_format',
        'Tên hiển thị phải có tối thiểu 2 từ',
        (value) => {
          if (value) {
            return value.split(' ').length >= 2;
          }

          return true;
        },
      ),
    quantity: Yup.string().trim()
      .required('Số lượng phòng không được để trống')
      .test(
        'quantity_format',
        'Số lượng phòng là số nguyên không nhỏ hơn 1',
        (value) => {
          if (value) {
            return +value > 0;
          }
          return true;
        },
      ),
    bedTypes: Yup.array()
      .test('bedTypes', 'Số lượng giường là số nguyên không nhỏ hơn 1', (value) => value.filter((bed) => bed.isChecked).every((bed) => bed.quantity > 0)),
    priceBase: Yup.string().trim().required('Giá cơ bản mỗi đêm không được để trống'),
    maxAdults: Yup.number()
      .typeError(getRequiredText('Số người lớn tối đa'))
      .required(getRequiredText('Số người lớn tối đa'))
      .min(1, 'Số lượng phải là số nguyên lớn hơn hoặc bằng 1'),
    maxChildren: Yup.number()
      .typeError(getRequiredText('Số trẻ em tối đa'))
      .required(getRequiredText('Số trẻ em tối đa'))
      .min(0, 'Số lượng phải là số nguyên lớn hơn hoặc bằng 0'),
    standardGuests: Yup.number()
      .typeError(getRequiredText('Số khách tiêu chuẩn'))
      .required(getRequiredText('Số khách tiêu chuẩn'))
      .min(1, 'Số lượng phải là số nguyên lớn hơn hoặc bằng 1'),
    maxCapacity: Yup.number()
      .typeError(getRequiredText('Sức chứa tối đa'))
      .required(getRequiredText('Sức chứa tối đa'))
      .min(1, 'Sức chứa tối đa phải lớn hơn hoặc bằng 1')
      .max(20, 'Sức chứa tối đa không được lớn hơn 20'),
  },
  stepAmenitiesService: {
    mainLanguages: Yup.array().test(
      'mainLanguages_required',
      'Vui lòng chọn ngôn ngữ',
      (value) => value?.length > 0,
    ),
  },
  stepAddImages: {
    images: Yup.array().test(
      'images required',
      'Ảnh không được để trống',
      (value) => value.length > 0,
    ).test(
      'images min and max',
      'Số lượng ảnh từ 4 đến 50 ảnh',
      (value) => value.length > 3 && value.length < 51,
    ),
  },

  stepPolicy: {
    checkInTime: Yup.string(),

    checkOutTime: Yup.string(),

    hasCurfew: Yup.boolean(),

    curfewFrom: Yup.string(),

    curfewTo: Yup.string(),
  },
};

export const editInformationLocateValidation = {
  name: Yup.string()
    .trim()
    .required('Tên chỗ nghỉ không được để trống')
    .max(100, 'Tên chỗ nghỉ không quá 100 ký tự'),
  placeType: Yup.string()
    .trim()
    .required('Loại hình không được để trống'),
  starRating: Yup.number(),
  province: Yup.string()
    .trim()
    .required('Tỉnh/ Thành phố không được để trống'),
  market: Yup.string()
    .trim()
    .required('Thị trường không được để trống'),
  // district: Yup.string()
  //   .trim()
  //   .required('Quận/ Huyện không được để trống'),
  district: Yup.string().trim(),
  ward: Yup.string()
    .trim(),
  address: Yup.string()
    .trim()
    .required('Địa chỉ không được để trống')
    .test(
      'address_format',
      'Địa chỉ không quá 200 ký tự',
      (value, ctx) => {
        if (!value || !value.length) return false;

        if (value.split(' ').length < 2) {
          return ctx.createError({ message: 'Địa chỉ gồm 2 từ trở lên' });
        }

        return value.length < 201;
      },
    ),
  coordinates: Yup.string().trim().required('Vui lòng nhập định dạng lat, long')
    .test(
      'coordinates_format',
      'Nhập lat trước long sau',
      (value, ctx) => {
        const coordinates = value?.split(',').map((coord) => coord.trim());

        if (coordinates?.length !== 2 || !coordinates[0] || !coordinates[1]) {
          return ctx.createError({ message: 'Định dạng gồm lat, long' });
        }

        const isLatitudeValid = /^[-+]?((1[0-7]|[1-9])?\d(\.\d+)?|180(\.0+)?)$/.test(coordinates[1]);
        const isLongitudeValid = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/.test(coordinates[0]);

        if (!isLatitudeValid || !isLongitudeValid
        ) {
          return ctx.createError({ message: 'Vui lòng nhập định dạng lat, long' });
        }

        return true;
      },
    ),
  description: Yup.string()
    .required('Giới thiệu chổ nghỉ không được để trống')
    .max(1500, 'Giới thiệu chỗ nghỉ không quá 1500 ký tự'),
  landmarks: Yup.array(),
};

export const addAlreadyLocationStepOneValidation = {
  email: Yup.string()
    .required(getRequiredText('Email'))
    .max(textLengthLimit.SHORT, getMaxText('Email', textLengthLimit.SHORT))
    .matches(EMAIL_REGEX, getInvalidText('Email')),

  password: Yup.string()
    .trim()
    .required(getRequiredText('Mật khẩu'))
    .min(
      textLengthLimit.PASSWORD,
      getInvalidLengthPassword(textLengthLimit.PASSWORD, textLengthLimit.PASSWORD_MAX),
    )
    .max(
      textLengthLimit.PASSWORD_MAX,
      getInvalidLengthPassword(textLengthLimit.PASSWORD, textLengthLimit.PASSWORD_MAX),
    )
    .matches(PASSWORD_IS_HAVE_SPECIAL, getInvalidText('Mật khẩu')),
};

export const addAlreadyLocationStepTwoValidation = {
  idLocation: Yup.string()
    .required(getRequiredText('Id chỗ nghỉ')),
};

export const addAlreadyLocationStepThreeValidation = {
  otpCode: Yup.string()
    .trim()
    .required(getRequiredText('Mã OTP'))
    .min(textLengthLimit.CODE, getMinText('Mã OTP', textLengthLimit.CODE))
    .max(textLengthLimit.CODE, getMaxText('Mã OTP', textLengthLimit.CODE))
    .matches(NUMBER_ONLY_REGEX, getInvalidText('Mã OTP')),
};

export const createNewRoomValidation = {
  roomName: Yup.string(),
  name: Yup.string()
    .required(getRequiredText('Tên phòng'))
    .min(2, 'Tên phải có ít nhất 2 ký tự')
    .nullable(),
  quantity: Yup.string()
    .required(getRequiredText('Số lượng phòng'))
    .test(
      'min_quantity',
      'Số lượng phòng là số nguyên lớn hơn 0 và nhỏ hơn 1000',
      (value) => parseCurrency(value) > 0 && parseCurrency(value) < 1000,
    ),
  totalBedrooms: Yup.string()
    .test(
      'min_total_bed_rooms',
      'Số lượng phòng ngủ là số nguyên lớn hơn 0 và nhỏ hơn 1000',
      (value) => {
        if (parseCurrency(value) === 0) {
          return true;
        }
        return parseCurrency(value) >= 1 && parseCurrency(value) < 1000;
      },
    ),
  totalLivingRooms: Yup.string()
    .test(
      'min_total_living_rooms',
      'Số lượng phòng khách là số nguyên lớn hơn 0 và nhỏ hơn 1000',
      (value) => {
        if (parseCurrency(value) === 0) {
          return true;
        }
        return parseCurrency(value) >= 1 && parseCurrency(value) < 1000;
      },
    ),
  totalBathrooms: Yup.string()
    .test(
      'min_total_bath_rooms',
      'Số lượng phòng tắm là số nguyên lớn hơn 0 và nhỏ hơn 1000',
      (value) => {
        if (parseCurrency(value) === 0) {
          return true;
        }
        return parseCurrency(value) >= 1 && parseCurrency(value) < 1000;
      },
    ),
  bedTypes: Yup.array()
    .test('bedTypes', 'Số lượng giường được chọn là số nguyên không nhỏ hơn 1', (value) => {
      if (value.length > 0) {
        const newValue = value.filter((bed) => (bed.checked));

        return newValue.every((newBed) => newBed.quantity > 0);
      }

      return false;
    })
    .test('bedTypes', 'Loại giường không được để trống', (value) => {
      if (!value || value.length === 0) {
        return false;
      }
      return value.some((bed) => bed.quantity > 0);
    })
    .test('bedTypes', 'Loại giường không được để trống', (value) => {
      const newValue = value.filter((bed) => (bed.checked));
      if (!newValue || newValue.length === 0) {
        return false;
      }
      return true;
    })
    .required(getRequiredText('Loại giường')),
  maxAdults: Yup.number()
    .typeError(getRequiredText('Số người lớn tối đa'))
    .required(getRequiredText('Số người lớn tối đa'))
    .min(1, 'Số lượng phải là số nguyên lớn hơn hoặc bằng 1')
    .test(
      'maxAdults_maxCapacity',
      'Số người lớn tối đa không được lớn hơn sức chứa tối đa',
      function testMaxAdults(value) {
        const { maxCapacity } = this.parent;
        if (
          value === undefined
          || value === null
          || maxCapacity === undefined
          || maxCapacity === null
        ) {
          return true;
        }
        return Number(value) <= Number(maxCapacity);
      },
    ),
  maxChildren: Yup.number()
    .typeError(getRequiredText('Số trẻ em tối đa'))
    .required(getRequiredText('Số trẻ em tối đa'))
    .min(0, 'Số lượng phải là số nguyên lớn hơn hoặc bằng 0')
    .test(
      'maxChildren_maxCapacity',
      'Số trẻ em tối đa không được lớn hơn sức chứa tối đa',
      function testMaxChildren(value) {
        const { maxCapacity } = this.parent;
        if (
          value === undefined
          || value === null
          || maxCapacity === undefined
          || maxCapacity === null
        ) {
          return true;
        }
        return Number(value) <= Number(maxCapacity);
      },
    ),
  standardGuests: Yup.number()
    .typeError(getRequiredText('Số khách tiêu chuẩn'))
    .required(getRequiredText('Số khách tiêu chuẩn'))
    .min(1, 'Số lượng phải là số nguyên lớn hơn hoặc bằng 1')
    .test(
      'standardGuests_maxCapacity',
      'Số khách tiêu chuẩn không được lớn hơn sức chứa tối đa',
      function testStandardGuests(value) {
        const { maxCapacity } = this.parent;
        if (
          value === undefined
          || value === null
          || maxCapacity === undefined
          || maxCapacity === null
        ) {
          return true;
        }
        return Number(value) <= Number(maxCapacity);
      },
    ),
  maxCapacity: Yup.number()
    .typeError(getRequiredText('Sức chứa tối đa'))
    .required(getRequiredText('Sức chứa tối đa'))
    .min(1, 'Sức chứa tối đa phải lớn hơn hoặc bằng 1')
    .max(20, 'Sức chứa tối đa không được lớn hơn 20'),
  maxCustomers: Yup.number()
    .min(0, 'Tổng số khách tối đa không được nhỏ hơn 0')
    .max(20, 'Tổng số khách tối đa không được lớn hơn 20'),
};

export const editRoomValidation = {
  allowDeposit: Yup.boolean().required('Yêu cầu chọn'),
  depositType: Yup.string().when('allowDeposit', {
    is: true,
    then: Yup.string().required('Vui lòng chọn loại hình đặt cọc'),
  }),
  amount: Yup.string().when(['allowDeposit', 'depositType'], {
    is: (allowDeposit, depositType) => allowDeposit && depositType === 'AMOUNT',
    then: Yup.string()
      .matches(NUMBER_WITH_PERIOD, {
        message: 'Vui lòng nhập vào số giá trị tối thiểu 100.000 VNĐ',
        excludeEmptyString: true,
      })
      .test({
        message: 'Vui lòng nhập vào số giá trị tối thiểu 100.000 VNĐ',
        test(value) {
          const number = parseCurrency(value);
          return number >= 100000;
        },
      }),
  }),
  depositPercent: Yup.string().when(['allowDeposit', 'depositType'], {
    is: (allowDeposit, depositType) => allowDeposit && depositType === 'PERCENT',
    then: Yup.string()
      .matches(NUMBER_WITH_PERIOD, {
        message: 'Vui lòng nhập phần trăm đặt cọc <=100%',
        excludeEmptyString: true,
      })
      .test({
        message: 'Vui lòng nhập phần trăm đặt cọc > 0% và <= 100%',
        test(value) {
          const number = parseCurrency(value);
          return number > 0 && number <= 100;
        },
      }),
  }),
  checkInTime: Yup.string(),
  checkOutTime: Yup.string(),
};

export const updateNumberAvailableRoom = {
  rooms: Yup.array()
    .test('rooms', 'Loại phòng không được để trống', (value) => value.filter((room) => room.isChecked).length > 0)
    .test('rooms', 'Số lượng phòng mở bán không được lớn hơn số lượng phòng hiện có', (value) => value.filter((room) => room.isChecked)
      .every((room) => +room.availableRooms <= room.quantity)),
  endDate: Yup.date().test('endDate', 'Ngày kết thúc không được để trống', (value) => !!value),
  weekDays: Yup.array()
    .test('weekDays', 'Thứ ngày không được để trống', (value) => value.filter((day) => day.isChecked).length > 0),
};

export const updateCloseOpenRoom = {
  rooms: Yup.array()
    .test('rooms', 'Loại phòng không được để trống', (value) => value.filter((room) => room.isChecked).length > 0),
  endDate: Yup.date().test('endDate', 'Ngày kết thúc không được để trống', (value) => !!value),
  weekDays: Yup.array()
    .test('weekDays', 'Thứ ngày không được để trống', (value) => value.filter((day) => day.isChecked).length > 0),
};

export const updatePriceRoom = {
  rooms: Yup.array()
    .test('rooms', 'Loại phòng không được để trống', (value) => value.filter((room) => room.isChecked).length > 0),
  endDate: Yup.date().test('endDate', 'Ngày kết thúc không được để trống', (value) => !!value),
  weekDays: Yup.array()
    .test('weekDays', 'Thứ ngày không được để trống', (value) => value.filter((day) => day.isChecked).length > 0),
  priceTypes: Yup.array()
    .min(1, 'Loại phòng không được để trống'),
  price: Yup.string()
    .required('Giá phòng không được để trống')
    .min(0, 'Giá phòng phải là số nguyên lớn hơn hoặc bằng 0'),
};

export const addPlacePriceTypes = {
  name: Yup.string()
    .required(getRequiredText('Tên loại giá')),
  rooms: Yup.array()
    .test('rooms', 'Loại phòng không được để trống', (value) => {
      if (!value || value.length === 0) {
        return false;
      }
      return true;
    }),
  includeMeals: Yup.array()
    .test('no-underscore', 'Bữa ăn không được để trống', (value) => {
      if (value && Array.isArray(value)) {
        return value.every((meal) => !meal.includes('_'));
      }
      return true;
    }),
  priceAdjustmentValue: Yup.number().when('priceAdjustmentType', {
    is: 'PERCENT',
    then: Yup.number().min(-100, 'Giá trị không được nhỏ hơn -100%').max(100, 'Giá trị không được lớn hơn 100%'),
  }),
};

export const updateChildExtraBedPolicy = {
  childrenPolicy: Yup.array()
    .test({
      message: 'Tuổi không được để trống',
      test(value) {
        const { allowChildrenPolicy } = this.parent;

        if (allowChildrenPolicy) {
          const checkNullAge = value.every((item) => (item.condition === 'BELOW' && item.childTo) || (item.condition === 'FROM' && item.childFrom && item.childTo) || (item.condition === 'ABOVE' && item.childFrom));

          if (!checkNullAge) return false;
        }

        return true;
      },
    })
    .test({
      message: 'Tuổi bắt đầu phải bé hơn tuổi kết thúc',
      test(value) {
        const { allowChildrenPolicy } = this.parent;

        if (allowChildrenPolicy) {
          const ageBelow = value.filter((item) => item.condition === 'FROM');

          if (!ageBelow.every((item) => +item.childFrom < +item.childTo)) {
            return false;
          }
        }

        return true;
      },
    }),
  extraBedPolicy: Yup.array()
    .test({
      message: 'Phòng không được để trống',
      test(value) {
        const { allowExtraBedPolicy } = this.parent;

        if (allowExtraBedPolicy) {
          const checkNullExtraBed = value.every((item) => !item.isChecked);

          if (checkNullExtraBed) return false;
        }

        return true;
      },
    })
    .test({
      message: 'Số lượng giường không được để trống',
      test(value) {
        const { allowExtraBedPolicy } = this.parent;

        if (allowExtraBedPolicy) {
          const filterChecked = value.filter((item) => item.isChecked);

          return filterChecked.every((item) => item.bedQuantity > 0);
        }

        return true;
      },
    })
    .test({
      message: 'Tuổi không được để trống',
      test(value) {
        const { allowExtraBedPolicy } = this.parent;

        if (allowExtraBedPolicy) {
          const filterFromAges = value.filter((item) => item.ageLimitCondition === 'FROM' && item.isChecked);

          const checkFromAge = filterFromAges.every((item) => item.childFrom && item.childTo);

          if (!checkFromAge) return false;
        }

        return true;
      },
    })
    .test({
      message: 'Tuổi bắt đầu phải lớn hơn tuổi kết thúc',
      test(value) {
        const { allowExtraBedPolicy } = this.parent;

        if (allowExtraBedPolicy) {
          const filterFromAges = value.filter((item) => item.ageLimitCondition === 'FROM' && item.isChecked);

          const checkFromAge = filterFromAges.every((item) => +item.childFrom < +item.childTo);

          if (!checkFromAge) return false;
        }

        return true;
      },
    })
    .test({
      message: 'Giá tiền không được để trống',
      test(value) {
        const { allowExtraBedPolicy } = this.parent;

        if (allowExtraBedPolicy) {
          const checkNullPrice = value.filter((item) => item.isChecked).every((item) => item.price);

          if (!checkNullPrice) return false;
        }

        return true;
      },
    }),
};

export const updateFacilities = {
  roomsSize: Yup.array()
    .test('roomsSize', 'Diện tích phòng không được để trống', (value) => value.every((item) => item.size)),
};

export const responseReviewUserValidation = {
  managerFeedback: Yup.string().required(getRequiredText('Phản hồi của bạn')).max(350, getMaxText('Phản hồi của bạn', 350)),
};

export const createPromotionValidation = {
  rooms: Yup.array()
    .test({
      message: 'Vui lòng thêm phòng vào chỗ nghỉ',
      test(value) {
        const { isAllRooms } = this.parent;

        if (!isAllRooms) return value.length;

        return true;
      },
    }),
  priceTypes: Yup.array()
    .test({
      message: 'Vui lòng chọn loại giá',
      test(value) {
        const { isAllPriceTypes } = this.parent;

        if (!isAllPriceTypes) return value.length;

        return true;
      },
    }),
  applyBookingDays: Yup.array()
    .test('applyBookingDays', 'Thứ ngày không được để trống', (value) => value.filter((day) => day.isChecked).length > 0),
  discountRate: Yup.string()
    .required('Phần trăm giảm giá không được để trống')
    .test({
      message: 'Vui lòng nhập vào số nguyên lớn hơn 0',
      test(value) {
        const number = parseCurrency(value);

        return number > 0;
      },
    })
    .test({
      message: 'Phần trăm giảm giá tối đa là 70%',
      test(value) {
        const number = parseCurrency(value);

        return number <= 70;
      },
    }),
  bookingStartAt: Yup.date()
    .test(
      'bookingStartAt',
      'Ngày bắt đầu không được để trống',
      (value) => value,
    )
    .test(
      'bookingStartAt',
      'Thời gian bắt đầu phải lớn hơn hoặc bằng thời gian hiện tại',
      (value) => {
        if (!value) return true;
        const currentDate = new Date().setHours(0, 0, 0, 0);

        return new Date(value).getTime() >= currentDate;
      },
    ),
  bookingEndAt: Yup.date()
    .test(
      'bookingEndAt',
      'Ngày kết thúc không được để trống',
      (value) => value,
    )
    .test({
      message: 'Thời gian kết thúc phải lớn hoặc bằng hơn thời gian bắt đầu',
      test(value) {
        const { bookingStartAt } = this.parent;

        if (!bookingStartAt || !value) return true;

        return value.setHours(0, 0, 0, 0) >= bookingStartAt.setHours(0, 0, 0, 0);
      },
    }),
  stayAt: Yup.array()
    .test(
      'stayAt',
      'Ngày cư trú không được để trống',
      (value) => value.every((item) => item.startAt && item.endAt),
    )
    .test(
      'stayAt',
      'Ngày cư trú phải lớn hơn ngày hiện tại',
      (value) => value.every(
        (item) => {
          if (!item.startAt || !item.endAt) return true;

          const currentDate = new Date().setHours(0, 0, 0, 0);

          return new Date(item.startAt).setHours(0, 0, 0, 0) >= currentDate
          && new Date(item.endAt).setHours(0, 0, 0, 0) >= currentDate;
        },
      ),
    )
    .test(
      'stayAt',
      'Ngày kết thúc phải lớn hơn ngày bắt đầu',
      (value) => value.every(
        (item) => {
          if (!item.startAt || !item.endAt) return true;

          return new Date(item.startAt).getTime() <= new Date(item.endAt).getTime();
        },
      ),
    )
    .test({
      message: 'Thời gian bắt đầu lưu trú phải lớn hơn hoặc bằng thời gian bắt đầu đặt',
      test(value) {
        const { bookingStartAt } = this.parent;

        if (!bookingStartAt && !value[0].startAt) return true;

        return new Date(value[0].startAt).setHours(0, 0, 0, 0)
        >= bookingStartAt.setHours(0, 0, 0, 0);
      },
    })
    .test({
      message: 'Thời gian kết thúc lưu trú phải lớn hơn hoặc bằng thời gian kết thúc đặt',
      test(value) {
        const { bookingEndAt } = this.parent;

        if (!bookingEndAt && !value[value.length - 1].endAt) return true;

        return new Date(value[value.length - 1].endAt).setHours(0, 0, 0, 0)
        >= bookingEndAt.setHours(0, 0, 0, 0);
      },
    })
    .test(
      'stayAt',
      'Thời gian lưu trú phải là ngày liên tục',
      (value) => {
        if (!value.length > 1) return true;

        const checkNull = value.every((item) => item.startAt && item.endAt);
        if (!checkNull) return true;

        return areDatesConsecutive(value);
      },
    ),
  name: Yup.string()
    .trim()
    .required('Tên khuyến mãi không được để trống')
    .max(200, 'Tên khuyến mãi không được vượt quá 200 ký tự'),
  minEarlyDays: Yup.number()
    .required('Số ngày không được để trống')
    .min(1, 'Số ngày phải lớn hơn 0'),
};

export const createLastMinutePromotionValidation = {
  applyTimes: Yup.object()
    .test({
      message: 'Thời gian phải có định dạng xx:00',
      test(value) {
        const { isRequired } = this.parent;
        const { from, to } = value;

        if (!isRequired) return true;

        if (from.split(':')[1] === '00' && to.split(':')[1] === '00') return true;

        return false;
      },
    })
    .test({
      message: 'Thời gian bắt đầu phải nhỏ hơn thời gian kết thúc',
      test(value) {
        const { isRequired } = this.parent;
        const { from, to } = value;

        if (!isRequired) return true;

        if (from.split(':')[0] > to.split(':')[0]) return false;

        if (from.split(':')[0] === to.split(':')[0] && from.split(':')[1] >= to.split(':')[1]) return false;

        return true;
      },
    }),
};

export const createLongtermPromotionValidation = {
  minStayNights: Yup.string()
    .test(
      'minStayNights',
      'Số đêm tối thiểu không hợp lệ',
      (value) => Number.isInteger(+value),
    )
    .test(
      'minStayNights',
      'Số đêm tối thiểu phải lớn hơn 0 và bé hơn 1000',
      (value) => +value > 0 && +value < 1000,
    ),
};

export const bankInfoValidation = {
  bankId: Yup.string()
    .required('Tên ngân hàng không được để trống'),
  accountNumber: Yup.string()
    .required('Số tài khoản không được để trống')
    .matches(NUMBER_ONLY, 'Số tài khoản không hợp lệ'),
  accountName: Yup.string()
    .required('Tên chủ tài khoản không được để trống')
    .matches(CHECK_MORE_THAN_2_WORDS, 'Tên chủ tài khoản tối thiểu 2 từ')
    .matches(UPPERCASE_WITH_SPACE_AND_NO_NUMBER_REGEX, 'Tên chủ tài khoản phải in hoa và không dấu'),
};

export const roomStatusValidation = {
  status: Yup.string()
    .test({
      message: 'Trạng thái không được để trống',
      test(value) {
        const { availableRooms } = this.parent;

        if (availableRooms > 0 && value === 'FULL') return false;

        return true;
      },
    }),
};

export const addUserAccountFormSteps = {
  fullName: Yup.string()
    .required(getRequiredText('Tên người dùng')),
  email: Yup.string()
    .required(getRequiredText('Email'))
    .max(textLengthLimit.SHORT, getMaxText('Email', textLengthLimit.SHORT))
    .matches(EMAIL_REGEX, getInvalidText('Email')),
};

export const createPassword = {
  email: Yup.string()
    .required(getRequiredText('Email'))
    .max(textLengthLimit.SHORT, getMaxText('Email', textLengthLimit.SHORT))
    .matches(EMAIL_REGEX, getInvalidText('Email')),

  password: Yup.string()
    .trim()
    .required(getRequiredText('Mật khẩu'))
    .min(
      textLengthLimit.PASSWORD,
      getInvalidLengthPassword(textLengthLimit.PASSWORD, textLengthLimit.PASSWORD_MAX),
    )
    .max(
      textLengthLimit.PASSWORD_MAX,
      getInvalidLengthPassword(textLengthLimit.PASSWORD, textLengthLimit.PASSWORD_MAX),
    )
    .matches(PASSWORD_IS_HAVE_SPECIAL, getInvalidText('Mật khẩu')),

  confirmPassword: Yup.string()
    .trim()
    .required(getRequiredText('Xác nhận lại mật khẩu'))
    .oneOf([Yup.ref('password'), null], getInvalidText('Xác nhận lại mật khẩu'))
    .min(
      textLengthLimit.PASSWORD,
      getInvalidLengthPassword(textLengthLimit.PASSWORD, textLengthLimit.PASSWORD_MAX),
    )
    .max(
      textLengthLimit.PASSWORD_MAX,
      getInvalidLengthPassword(textLengthLimit.PASSWORD, textLengthLimit.PASSWORD_MAX),
    ),
};

export const addContactsInfo = {
  name: Yup.string()
    .required(getRequiredText('Tên người liên hệ'))
    .max(50, getMaxText('Tên người liên hệ', 50)),
  gender: Yup.string(),
  mainLanguage: Yup.string()
    .required(getRequiredText('Ngôn ngữ ưu tiên')),
  role: Yup.string()
    .required(getRequiredText('Chức vụ')),
  email: Yup.string()
    .required(getRequiredText('Email'))
    .max(textLengthLimit.SHORT, getMaxText('Email', textLengthLimit.SHORT))
    .matches(EMAIL_REGEX, getInvalidText('Email')),
  phoneNumbers: Yup.array()
    .of(
      Yup.string()
        .trim()
        .matches(PHONE_REGEX, getInvalidText('Số điện thoại'))
        .test('is-not-empty', getRequiredText('Số điện thoại'), (value) => value && value.length > 0),
    )
    .min(1, getRequiredText('Số điện thoại'))
    .test('unique-phone-numbers', 'Số điện thoại không được trùng lặp', (value) => value && new Set(value).size === value.length),
};
